import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PermissionMatrix } from "@/components/permissions/PermissionMatrix";
import { RoleManagement } from "@/components/permissions/RoleManagement";
import { ComponentPermissionPanel } from "@/components/permissions/ComponentPermissionPanel";
import { usePermissions } from "@/contexts/PermissionContext";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useParams } from "react-router-dom";
import {
  Shield,
  Users,
  Settings,
  Eye,
  Lock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  User,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ResourceType,
  PermissionAction,
  SystemRole,
} from "@/types/permissions";

interface EditorPermissionManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditorPermissionManager: React.FC<
  EditorPermissionManagerProps
> = ({ isOpen, onClose }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { systemRole, checkPermission, refreshPermissions, isLoading, error } =
    usePermissions();
  const { selectedComponents, getSelectedComponents } = useEditorStore();

  const [activeTab, setActiveTab] = useState("overview");
  const [permissionStats, setPermissionStats] = useState({
    totalUsers: 0,
    activeCollaborators: 0,
    permissionViolations: 0,
    lastUpdate: new Date(),
  });

  const selectedComponentObjects = getSelectedComponents();

  // Real-time permission monitoring
  useEffect(() => {
    if (!isOpen || !projectId) return;

    const checkProjectPermissions = async () => {
      try {
        // Check various permission levels
        const checks = [
          checkPermission({
            resource: { type: ResourceType.PROJECT, id: projectId },
            action: PermissionAction.READ,
          }),
          checkPermission({
            resource: { type: ResourceType.PROJECT, id: projectId },
            action: PermissionAction.UPDATE,
          }),
          checkPermission({
            resource: { type: ResourceType.PROJECT, id: projectId },
            action: PermissionAction.SHARE,
          }),
          checkPermission({
            resource: { type: ResourceType.PROJECT, id: projectId },
            action: PermissionAction.MANAGE_PERMISSIONS,
          }),
        ];

        const results = await Promise.all(checks);
        console.log("Permission check results:", results);

        // Update stats (mock data for now)
        setPermissionStats({
          totalUsers: 8,
          activeCollaborators: 3,
          permissionViolations: 0,
          lastUpdate: new Date(),
        });
      } catch (error) {
        console.error("Failed to check permissions:", error);
      }
    };

    checkProjectPermissions();

    // Refresh every 30 seconds
    const interval = setInterval(checkProjectPermissions, 30000);
    return () => clearInterval(interval);
  }, [isOpen, projectId, checkPermission]);

  const handleRefreshPermissions = useCallback(async () => {
    try {
      await refreshPermissions();
    } catch (error) {
      console.error("Failed to refresh permissions:", error);
    }
  }, [refreshPermissions]);

  const getRoleIcon = (role: SystemRole) => {
    switch (role) {
      case SystemRole.SUPER_ADMIN:
        return <Shield className="w-4 h-4 text-red-500" />;
      case SystemRole.ORGANIZATION_ADMIN:
        return <Shield className="w-4 h-4 text-orange-500" />;
      case SystemRole.TEAM_LEAD:
        return <Users className="w-4 h-4 text-blue-500" />;
      case SystemRole.DESIGNER:
        return <Settings className="w-4 h-4 text-green-500" />;
      case SystemRole.VIEWER:
        return <Eye className="w-4 h-4 text-gray-500" />;
      default:
        return <Lock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getPermissionStatusColor = (hasPermission: boolean) => {
    return hasPermission ? "text-green-600" : "text-red-600";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Project Permissions
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 overflow-hidden"
        >
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="matrix">Permission Matrix</TabsTrigger>
            <TabsTrigger value="roles">Role Management</TabsTrigger>
            <TabsTrigger value="components">Components</TabsTrigger>
          </TabsList>

          <div className="mt-4 overflow-hidden flex-1">
            <TabsContent value="overview" className="h-full overflow-hidden">
              <ScrollArea className="h-[60vh]">
                <div className="space-y-6">
                  {/* Permission Status Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                          Current Role
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          {systemRole ? (
                            getRoleIcon(systemRole)
                          ) : (
                            <User className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="font-medium">
                            {systemRole
                              ?.replace(/_/g, " ")
                              .toLowerCase()
                              .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                              "Loading..."}
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                          Team Members
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-500" />
                          <span className="font-medium">
                            {permissionStats.totalUsers}
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            {permissionStats.activeCollaborators} active
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                          Permission Issues
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          {permissionStats.permissionViolations > 0 ? (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                          <span className="font-medium">
                            {permissionStats.permissionViolations}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            issues
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                          Last Updated
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">
                            {permissionStats.lastUpdate.toLocaleTimeString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Quick Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Quick Actions</CardTitle>
                      <CardDescription>
                        Common permission management tasks
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRefreshPermissions}
                          disabled={isLoading}
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          Refresh Permissions
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveTab("matrix")}
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          View Permission Matrix
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveTab("roles")}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Manage Roles
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Current Project Permissions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">
                        Your Project Permissions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          {
                            action: "Read",
                            key: PermissionAction.READ,
                            description: "View project content",
                          },
                          {
                            action: "Edit",
                            key: PermissionAction.UPDATE,
                            description: "Modify components and content",
                          },
                          {
                            action: "Share",
                            key: PermissionAction.SHARE,
                            description: "Share project with others",
                          },
                          {
                            action: "Delete",
                            key: PermissionAction.DELETE,
                            description: "Delete project components",
                          },
                          {
                            action: "Manage Team",
                            key: PermissionAction.MANAGE_MEMBERS,
                            description: "Add/remove team members",
                          },
                          {
                            action: "Manage Permissions",
                            key: PermissionAction.MANAGE_PERMISSIONS,
                            description: "Edit user permissions",
                          },
                        ].map(({ action, key, description }) => (
                          <div
                            key={action}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div>
                              <div className="font-medium text-sm">
                                {action}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {description}
                              </div>
                            </div>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {error && (
                    <Alert variant="destructive">
                      <AlertTriangle className="w-4 h-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="matrix" className="h-full">
              <PermissionMatrix
                projectId={projectId}
                onPermissionChange={(cell) => {
                  console.log("Permission changed:", cell);
                  handleRefreshPermissions();
                }}
              />
            </TabsContent>

            <TabsContent value="roles" className="h-full">
              <RoleManagement
                projectId={projectId}
                onRoleChange={(role) => {
                  console.log("Role changed:", role);
                  handleRefreshPermissions();
                }}
              />
            </TabsContent>

            <TabsContent value="components" className="h-full">
              <ScrollArea className="h-[60vh]">
                <div className="space-y-4">
                  {selectedComponentObjects.length > 0 ? (
                    selectedComponentObjects.map((component) => (
                      <ComponentPermissionPanel
                        key={component.id}
                        componentId={component.id}
                        componentName={component.name}
                        componentType={component.type}
                        onPermissionUpdate={() => handleRefreshPermissions()}
                      />
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-6 text-center">
                        <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">
                          Select a component to manage its permissions
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default EditorPermissionManager;
